import './Sidebar.scss';
import { useSelector } from 'react-redux';
import { RolesEnum } from 'enums';
import SidebarLink from './SidebarLink';
// import { LANGUAGES } from 'config/i18n';
import { useTranslation } from 'react-i18next';
// import { I18n } from 'aws-amplify';
import { useLocation } from 'react-router-dom';
import { DEPLOYMENT_BASE_URL, FRONT_ROUTER } from 'constants/router';
import { selectUser } from 'store/slices/auth';

/**
 * @description Navigation component for Super Admin users.
 * Displays links to companies and users management pages.
 *
 * @SADtrace SAD-003-2.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @returns {JSX.Element} Super admin navigation links
 */
const SuperAdminTopNavigation = () => {
  const { t } = useTranslation();
  return (
    <>
      <SidebarLink
        title={t('sidebar.companies')}
        link="/companies"
        icon="building"
        testid="super"
      />
      <SidebarLink title={t('sidebar.users')} link="/users" icon="users" />
    </>
  );
};

/**
 * @description Navigation component for Regular Admin users.
 * Displays links to hospital, device, reports, and deployment pages with active state detection.
 *
 * @SADtrace SAD-003-3.1, SAD-003-4.1, SAD-003-5.1, SAD-003-6.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {Object} props - Component props
 * @param {string} props.pathname - Current URL pathname to determine active links
 *
 * @returns {JSX.Element} Regular admin navigation links
 */
const RegularAdminTopNavigation = ({ pathname }: { pathname: string }) => {
  const { t } = useTranslation();
  const isHospitalActive = !![
    '/facilities',
    '/care-areas',
    '/drugs',
    '/advisories',
    '/therapies',
  ].filter((route) => !!pathname && pathname.includes(route)).length;

  return (
    <>
      <SidebarLink
        title={t('sidebar.hospital')}
        link="/facilities"
        icon="building"
        activeLink={isHospitalActive}
        testid="regular"
      />
      <SidebarLink title={t('sidebar.device')} link="/device" icon="device" />
      <SidebarLink
        title={t('sidebar.reports')}
        link="/reports"
        icon="reports"
      />
      <SidebarLink
        title={t('sidebar.deployment')}
        link={FRONT_ROUTER.DEPLOYMENT_FULL_DEPLOYMENT_TAB}
        icon="deployment"
        activeLink={pathname.includes(DEPLOYMENT_BASE_URL)}
      />
    </>
  );
};

/**
 * @description Main sidebar navigation component.
 * Renders different navigation links based on user role (Super Admin vs Regular Admin).
 * Displays help link in the footer section.
 *
 * @SADtrace SAD-003-1.1, SAD-003-2.1, SAD-003-3.1, SAD-003-4.1, SAD-003-5.1, SAD-003-6.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @returns {JSX.Element} Sidebar navigation component
 */
const Sidebar = () => {
  // const { i18n } = useTranslation();
  const user = useSelector(selectUser);
  const isSuperAdmin = !!user && user?.role === RolesEnum.SUPER_ADMIN;
  const { pathname } = useLocation();
  const { t } = useTranslation();

  return (
    <nav className="nav-sidebar" data-testid="sidebar">
      <div className="nav-sidebar__links">
        {isSuperAdmin ? (
          <SuperAdminTopNavigation />
        ) : (
          <RegularAdminTopNavigation pathname={pathname} />
        )}
      </div>

      <div className="footer-sidebar">
        <SidebarLink
          title={t('sidebar.help')}
          link={FRONT_ROUTER.HELP}
          icon="help"
          testid="help-link"
        />
        {/* <div className="nav-sidebar__languages py-2">
          {Object.keys(LANGUAGES).map((lng: string) => {
            return (
              <button
                className="nav-sidebar__languages-btn"
                key={lng}
                onClick={() => {
                  i18n.changeLanguage(lng);
                  I18n.setLanguage(lng);
                }}
                disabled={i18n.resolvedLanguage === lng}
              >
                {LANGUAGES[lng as keyof typeof LANGUAGES]}
              </button>
            );
          })}
        </div> */}
      </div>
    </nav>
  );
};

export default Sidebar;
